<template>
    <main>
        <SideBar 
            :current-language="prismicLang"
            :clc-name="clcName"
            @toggle-language="toggleLanguage"
        />
        <section class="CLCSite">
            <h1 class="center-wrap" :data-lang="prismicLang" v-html="bannerData.title"></h1>
            <ul class="grid-quick-links" v-if="bannerData.icons && bannerData.icons.length > 0">
                <li v-for="(icon, index) in bannerData.icons" :key="index" >
                    <a 
                        :href="getCategoryURL(icon)" 
                        :target="getCategoryIfTargetBlank(icon)" 
                        :title="getCategoryTitle(icon)" 
                        :data-label="`${getCategoryTitle(icon)} ${getCategoryDescription(icon)}`">
                            <img :src="getCategoryImage(icon)" :alt="getCategoryImageAlt(icon)" />
                            <h2>{{ getCategoryTitle(icon) }}</h2>
                            <p v-if="getCategoryDescription(icon)">{{ getCategoryDescription(icon) }}</p>
                    </a>
                </li>
            </ul>

            <div class="nav-web" :data-lang="prismicLang">
                <a href="https://www.google.com/" target="_blank" rel="noopener noreferrer">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M10.0514 9.06045L13.5586 12.5684C13.8315 12.8413 13.8324 13.2828 13.5564 13.5589C13.2823 13.8331 12.8339 13.8291 12.566 13.5612L9.05876 10.0533C6.85588 11.768 3.66946 11.6128 1.64466 9.58759C-0.548221 7.3943 -0.548221 3.83827 1.64466 1.64497C3.83755 -0.548324 7.39291 -0.548324 9.5858 1.64497C11.6106 3.67015 11.7658 6.85717 10.0514 9.06045ZM8.59315 8.59477C10.2378 6.94979 10.2378 4.28277 8.59315 2.6378C6.94849 0.992828 4.28197 0.992828 2.6373 2.6378C0.992642 4.28277 0.992642 6.94979 2.6373 8.59477C4.28197 10.2397 6.94849 10.2397 8.59315 8.59477Z" fill="#5B5B5B"/>
                    </svg>
                    <span>{{ translations.navWebText }}</span>
                </a>    
            </div>
            <div class="anchor-wrapper">
                <a href="#news" :data-lang="prismicLang" @click.prevent="scrollToNews">
                    <span class="text">{{ translations.seeWhatsNew }}</span>
                    <span class="arrow-down">
                        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                            <path d="M6.15398 9.23071L12.3078 15.3846L18.4617 9.23071" stroke="#009FDB" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </span>
                </a>
            </div>

            <!-- Cintillo / Banner -->
            <div v-if="cintilloData.enabled && (cintilloData.imageDesktop || cintilloData.imageMobile)" class="Banner-clc">
                <a
                    v-if="cintilloData.link"
                    :href="cintilloData.link"
                    :alt="cintilloData.title"
                    :target="cintilloData.blank ? '_blank' : '_self'"
                    :data-label="`Minibanner - ${cintilloData.title}`"
                >
                    <picture>
                        <source
                            v-if="cintilloData.imageMobile"
                            media="(max-width: 767px)"
                            :srcset="cintilloData.imageMobile"
                        />
                        <img
                            :src="cintilloData.imageDesktop"
                            :alt="cintilloData.title"
                        />
                    </picture>
                </a>
                <picture v-else>
                    <source
                        v-if="cintilloData.imageMobile"
                        media="(max-width: 767px)"
                        :srcset="cintilloData.imageMobile"
                    />
                    <img
                        :src="cintilloData.imageDesktop"
                        :alt="cintilloData.title"
                    />
                </picture>
            </div>

            <div id="news">
                <h3>{{ translations.newsTitle }}</h3>
                <div class="tag-bar">
                    <ul class="tags-filtros">
                        <li 
                            v-for="(tag, idx) in tags" 
                            :key="tag.id" 
                            :class="isActiveTag[idx] ? 'active' : 'in-active'" 
                            @click="toggleTag(idx)"
                            :data-label="`${prismicLang === 'en-us' ? 'Category' : 'Categoria'} ${tag.name}`"
                        >
                            <span>{{ tag.name }}</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none">
                                <circle cx="11.5" cy="11.5" r="11.5" fill="white"/>
                                <path d="M16.8188 6.46875L6.46875 16.8188" stroke="#5B5B5B" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                                <path d="M6.46875 6.46875L16.8188 16.8188" stroke="#5B5B5B" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </li>
                    </ul>
                </div>

                <!-- News grid -->
                <div
                    class="news-grid"
                    :class="filteredNewsItems.length > 0 ? 'min-height-false' : 'min-height-true'"
                >
                    <!-- Empty state - Diferentes casos -->
                    <div v-if="!isLoadingInitialNews && newsData !== null && ((hasActiveFilters && filteredNewsItems.length === 0) || (!hasActiveFilters && newsData.length === 0))" class="news-grid--empty-state">
                        <div class="empty-state-content">
                            <div class="empty-state-icon">😔</div>
                            <h2 class="empty-state-title">{{ hasActiveFilters ? translations.noResults : translations.noNewsAvailable }}</h2>
                            <p class="empty-state-message">{{ hasActiveFilters ? translations.noResultsMessage : translations.noNewsMessage }}</p>
                            <button v-if="hasActiveFilters" @click="clearAllFilters" class="empty-state-button">
                                {{ translations.clearFilters }}
                            </button>
                        </div>
                    </div>

                    <!-- News items -->
                    <div
                        class="news-grid--cell"
                        v-for="newsItem in filteredNewsItems"
                        :key="newsItem.id"
                    >
                        <div class="news-card">
                            <!-- Ribbon - TODO: This is NEW in Rebrand, will be added later -->
                            <div v-if="newsItem.ribbon" class="ribon">
                                {{ newsItem.ribbon }}
                            </div>

                            <a 
                                :href="getNewsUrl(newsItem.id)" 
                                :title="newsItem.title"
                                :data-label="`${newsItem.cta} - ${newsItem.title}`">
                                    <img
                                        :src="newsItem.image"
                                        :alt="newsItem.imageAlt"
                                    />
                            </a>

                            <div class="content">
                                <div class="top">
                                    <h4>{{ newsItem.title }}</h4>
                                </div>
                                <div class="bottom">
                                    <a
                                        :href="getNewsUrl(newsItem.id)"
                                        class="link"
                                        :title="newsItem.cta"
                                        :data-label="`${newsItem.cta} - ${newsItem.title}`"
                                    >
                                        {{ newsItem.cta }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Infinite Loading -->
                <infinite-loading
                    v-if="newsData && newsData.length > 0"
                    @infinite="infiniteHandler"
                    spinner="spiral"
                >
                    <!-- Custom Loader -->
                    <div slot="spinner" class="custom-loader">
                        <svg width="51" height="48" viewBox="0 0 51 48" fill="none" xmlns="http://www.w3.org/2000/svg" class="spinner-svg">
                            <circle cx="26.3999" cy="24" r="22" stroke="#009FDB" stroke-width="4"/>
                            <circle cx="5.6" cy="19.9999" r="5.6" fill="#009FDB"/>
                        </svg>
                    </div>

                    <!-- No more results -->
                    <div slot="no-more" class="news-grid--footer">
                        <p class="news-grid--end-message">
                            {{ translations.noMoreNews }}
                        </p>
                    </div>

                    <!-- No results -->
                    <div slot="no-results"></div>
                </infinite-loading>
            </div>
        </section>

        <footer>
            <a class="go-top" href="#" @click.prevent="scrollToTop" alt="Go Top">

                <svg v-if="prismicLang === 'en-us'" width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 40C0 17.9086 17.9086 0 40 0C62.0914 0 80 17.9086 80 40C80 62.0914 62.0914 80 40 80C17.9086 80 0 62.0914 0 40Z" fill="#49EEDC"/>
                <path d="M51.125 33.875L39.875 22.625L28.625 33.875" stroke="#0057B8" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M29.7659 48.045H26.6719V46.575H34.4699V48.045H31.3759V56.375H29.7659V48.045ZM39.7217 47.933C39.1244 47.933 38.597 48.087 38.1397 48.395C37.6824 48.703 37.3324 49.1277 37.0897 49.669C36.847 50.201 36.7257 50.803 36.7257 51.475C36.7257 52.175 36.8564 52.7957 37.1177 53.337C37.379 53.8783 37.7337 54.2937 38.1817 54.583C38.639 54.8723 39.1524 55.017 39.7217 55.017C40.319 55.017 40.8464 54.863 41.3037 54.555C41.761 54.247 42.111 53.827 42.3537 53.295C42.5964 52.7537 42.7177 52.147 42.7177 51.475C42.7177 50.775 42.587 50.1543 42.3257 49.613C42.0644 49.0717 41.705 48.6563 41.2477 48.367C40.7997 48.0777 40.291 47.933 39.7217 47.933ZM39.7217 46.421C40.6364 46.421 41.4437 46.645 42.1437 47.093C42.853 47.541 43.399 48.1477 43.7817 48.913C44.1644 49.6783 44.3557 50.5323 44.3557 51.475C44.3557 52.4643 44.1504 53.3463 43.7397 54.121C43.329 54.8957 42.7737 55.493 42.0737 55.913C41.3737 56.3237 40.5897 56.529 39.7217 56.529C38.807 56.529 37.995 56.305 37.2857 55.857C36.5857 55.409 36.0444 54.8023 35.6617 54.037C35.279 53.2717 35.0877 52.4177 35.0877 51.475C35.0877 50.4857 35.293 49.6037 35.7037 48.829C36.1144 48.0543 36.6697 47.4617 37.3697 47.051C38.0697 46.631 38.8537 46.421 39.7217 46.421ZM46.1158 46.575H49.4758C50.6051 46.575 51.4824 46.855 52.1078 47.415C52.7424 47.9657 53.0598 48.7403 53.0598 49.739C53.0598 50.747 52.7378 51.5403 52.0938 52.119C51.4591 52.6977 50.5864 52.987 49.4758 52.987H47.7258V56.375H46.1158V46.575ZM47.7258 51.573H49.3778C50.0218 51.573 50.5211 51.4143 50.8758 51.097C51.2398 50.7703 51.4218 50.3177 51.4218 49.739C51.4218 49.179 51.2444 48.7497 50.8898 48.451C50.5351 48.143 50.0311 47.989 49.3778 47.989H47.7258V51.573Z" fill="#0057B8"/>
                </svg>

                <svg v-else width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 40C0 17.9086 17.9086 0 40 0C62.0914 0 80 17.9086 80 40C80 62.0914 62.0914 80 40 80C17.9086 80 0 62.0914 0 40Z" fill="#49EEDC"/>
                <path d="M51.125 33.875L39.875 22.625L28.625 33.875" stroke="#0057B8" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M24.2481 56.529C22.9415 56.529 21.8308 56.249 20.9161 55.689L21.3781 54.359C22.2648 54.8163 23.1935 55.045 24.1641 55.045C24.8268 55.045 25.3355 54.9237 25.6901 54.681C26.0541 54.4383 26.2361 54.093 26.2361 53.645C26.2361 53.4023 26.1801 53.2017 26.0681 53.043C25.9655 52.8843 25.7788 52.7397 25.5081 52.609C25.2468 52.4783 24.8315 52.329 24.2621 52.161L23.6741 51.993C22.8061 51.7503 22.1761 51.4143 21.7841 50.985C21.3921 50.5463 21.1961 49.9723 21.1961 49.263C21.1961 48.3857 21.4995 47.695 22.1061 47.191C22.7128 46.6777 23.5435 46.421 24.5981 46.421C25.1395 46.421 25.6621 46.4863 26.1661 46.617C26.6701 46.7383 27.1275 46.9203 27.5381 47.163L27.1041 48.465C26.7495 48.2877 26.3621 48.1523 25.9421 48.059C25.5221 47.9563 25.1068 47.905 24.6961 47.905C24.0988 47.905 23.6368 48.017 23.3101 48.241C22.9928 48.4557 22.8341 48.773 22.8341 49.193C22.8341 49.529 22.9555 49.7997 23.1981 50.005C23.4501 50.2103 23.8888 50.4017 24.5141 50.579L25.1021 50.747C25.7741 50.943 26.3108 51.1623 26.7121 51.405C27.1135 51.6383 27.4075 51.9277 27.5941 52.273C27.7808 52.609 27.8741 53.0337 27.8741 53.547C27.8741 54.471 27.5475 55.199 26.8941 55.731C26.2501 56.263 25.3681 56.529 24.2481 56.529ZM33.3853 56.529C32.1533 56.529 31.2153 56.1837 30.5713 55.493C29.9367 54.8023 29.6193 53.7943 29.6193 52.469V46.575H31.2293V52.427C31.2293 53.3043 31.4067 53.9577 31.7613 54.387C32.116 54.807 32.6573 55.017 33.3853 55.017C34.1133 55.017 34.6547 54.807 35.0093 54.387C35.364 53.9577 35.5413 53.3043 35.5413 52.427V46.575H37.1513V52.469C37.1513 53.7943 36.8293 54.8023 36.1853 55.493C35.5507 56.1837 34.6173 56.529 33.3853 56.529ZM39.2183 46.575H42.9283C43.9923 46.575 44.8043 46.7943 45.3643 47.233C45.9243 47.6717 46.2043 48.3063 46.2043 49.137C46.2043 49.6597 46.0596 50.1217 45.7703 50.523C45.4903 50.915 45.103 51.195 44.6083 51.363C45.215 51.4843 45.6816 51.7457 46.0083 52.147C46.335 52.5483 46.4983 53.057 46.4983 53.673C46.4983 54.2237 46.3583 54.7043 46.0783 55.115C45.8076 55.5257 45.425 55.8383 44.9303 56.053C44.4356 56.2677 43.8616 56.375 43.2083 56.375H39.2183V46.575ZM40.8283 52.119V55.031H43.2083C43.7216 55.031 44.123 54.905 44.4123 54.653C44.711 54.3917 44.8603 54.0323 44.8603 53.575C44.8603 53.099 44.7063 52.7397 44.3983 52.497C44.0903 52.245 43.6376 52.119 43.0403 52.119H40.8283ZM40.8283 47.919V50.775H42.9283C43.451 50.775 43.857 50.649 44.1463 50.397C44.445 50.145 44.5943 49.7903 44.5943 49.333C44.5943 48.8757 44.4496 48.5257 44.1603 48.283C43.871 48.0403 43.4603 47.919 42.9283 47.919H40.8283ZM48.1597 46.575H49.7697V56.375H48.1597V46.575ZM55.2941 52.791H53.5021V56.375H51.8921V46.575H55.3641C56.4655 46.575 57.3195 46.8457 57.9261 47.387C58.5328 47.919 58.8361 48.6703 58.8361 49.641C58.8361 50.3317 58.6635 50.929 58.3181 51.433C57.9821 51.937 57.5061 52.3057 56.8901 52.539L59.2701 56.375H57.4221L55.2941 52.791ZM53.5021 51.377H55.3501C55.9195 51.377 56.3675 51.223 56.6941 50.915C57.0301 50.607 57.1981 50.1917 57.1981 49.669C57.1981 49.1463 57.0348 48.7357 56.7081 48.437C56.3815 48.1383 55.9288 47.989 55.3501 47.989H53.5021V51.377Z" fill="#0057B8"/>
                </svg>

            </a>
            <p>
                <img src="../assets/images/rebrand-2026/logo-footer.png" alt="ATT Logo" />
                <span>Corporate Responsibility</span>
            </p>
            <ul>
                <li>
                    <a href="https://www.att.com/" data-label="AT&T" target="_blank" rel="noopener" alt="AT&T">AT&T</a>
                </li>
                <li>
                    <a href="https://sustainability.att.com/" :data-label="`${prismicLang === 'en-us' ? 'Sustainability' : 'Sostenibilidad'} ATT`" target="_blank" rel="noopener" :alt="`${prismicLang === 'en-us' ? 'Sustainability' : 'Sostenibilidad'} ATT`">{{ prismicLang === 'en-us' ? 'SUSTAINABILITY' : 'SOSTENIBILIDAD' }} </a>
                </li>
            </ul>
        </footer>
    </main>
</template>

<script>
import SideBar from './SideBar.vue';
import Const from '../constants';
import InfiniteLoading from 'vue-infinite-loading';
import { getCleanPrismicImageUrl } from './helpers/prismicHelpers';

export default {
    name: 'CLCHomePageRebrand',
    components: {
        SideBar,
        InfiniteLoading
    },
    data() {
        return {
            prismicLang: 'en-us',
            clcName: '',

            // Prismic data
            homePageDocument: null,
            newsDocument: null,
            homePageData: null,
            newsData: null,

            // Processed data from Prismic
            bannerData: {
                title: '',
                description: '',
                icons: [],
            },
            quicklinksData: {
                fullyLoaded: false,
                title: '',
                items: [],
            },
            cintilloData: {
                enabled: false,
                title: '',
                description: '',
                link: '',
                blank: false,
                linkText: '',
                imageDesktop: '',
                imageMobile: ''
            },

            clcSegmentValue: '',
            currentPage: 1,
            isLoadingInitialNews: true, // Estado de carga inicial de noticias

            // Tags base (bilingüe) - TODO: These will remain hardcoded for now as they are NEW in Rebrand
            // prismicCategory: valor exacto que viene de Prismic en item.data.categories[0].category
            tagsBase: [
                { id: 1, nameEN: 'Events', nameES: 'Eventos', prismicCategory: 'Events' },
                { id: 2, nameEN: 'Studying tips', nameES: 'Consejos de estudio', prismicCategory: 'Studying tips' },
                { id: 3, nameEN: 'Partnerships', nameES: 'Alianzas', prismicCategory: 'Partnership' },
                { id: 4, nameEN: 'Features', nameES: 'Funcionalidades', prismicCategory: 'Features' },
                { id: 5, nameEN: 'Success stories', nameES: 'Casos de éxito', prismicCategory: 'Success stories' },
                { id: 6, nameEN: 'Learning essentials', nameES: 'Recursos esenciales', prismicCategory: 'Learning essentials' }
            ],
            isActiveTag: [],
        }
    },
    
    computed: {
        newsDataHasItems() {
            return this.newsData?.length > 0;
        },

        translations() {
            const texts = {
                'en-us': {
                    // META
                    pageTitle: 'Connected Learning Centers',
                    pageDescription: 'Welcome to your connected learning experience!',

                    switchLanguage: 'Cambiar a Español',
                    logoAlt: 'ATT Connected Learning Center',
                    navWebText: 'Start your Internet search here',
                    seeWhatsNew: "See what's new", // TODO: This is NEW in Rebrand

                    // NEWS SECTION - TODO: These are NEW in Rebrand
                    newsTitle: 'News & Updates',
                    noResults: 'No news found',
                    noResultsMessage: 'Try adjusting or removing the filters to see more content.',
                    noNewsAvailable: 'No news available',
                    noNewsMessage: 'There are no news articles available at the moment. Please check back later.',
                    clearFilters: 'Clear all filters',
                    noMoreNews: "You're all caught up!",
                    learnMore: 'Learn more'
                },
                'es-es': {
                    // META
                    pageTitle: 'Centros de aprendizaje conectados',
                    pageDescription: '¡Te damos la bienvenida al aprendizaje online!',

                    switchLanguage: 'Switch to English',
                    logoAlt: 'Centro de aprendizaje conectado de ATT',
                    navWebText: 'Comienza tu búsqueda en Internet aquí',
                    seeWhatsNew: 'Mira lo que hay de nuevo', // TODO: This is NEW in Rebrand

                    // NEWS SECTION - TODO: These are NEW in Rebrand
                    newsTitle: 'Noticias y Actualizaciones',
                    noResults: 'No se encontraron noticias',
                    noResultsMessage: 'Intenta ajustar o eliminar los filtros para ver más contenido.',
                    noNewsAvailable: 'No hay noticias disponibles',
                    noNewsMessage: 'No hay noticias disponibles en este momento. Por favor, vuelve más tarde.',
                    clearFilters: 'Limpiar filtros',
                    noMoreNews: 'Estás al día; ya has visto todas las noticias.',
                    learnMore: 'Leer más'
                }
            };
            return texts[this.prismicLang] || texts['en-us'];
        },

        tags() {
            // Tags con nombre apropiado según idioma
            const isEnglish = this.prismicLang === 'en-us';
            return this.tagsBase.map(tag => ({
                id: tag.id,
                name: isEnglish ? tag.nameEN : tag.nameES,
                apiName: tag.nameEN,
                prismicCategory: tag.prismicCategory
            }));
        },

        hasActiveFilters() {
            return this.isActiveTag.some(active => active);
        },

        filteredNewsItems() {
            // Mapear los items de Prismic
            let items = (this.newsData || [])
                // Filtrar solo noticias que tengan al menos un slice en body
                .filter(item => item.data?.body && item.data.body.length > 0)
                .map(item => {
                    // Extraer la categoría de Prismic (viene en item.data.categories[0].category)
                    const prismicCategory = item.data?.categories?.[0]?.category || '';

                    return {
                        id: item.uid,
                        title: item.data.news_title?.[0]?.text || '',
                        image: item.data.news_image?.url || '',
                        imageAlt: item.data.news_image?.alt || '',
                        cta: item.data.news_link_text || (this.prismicLang === 'en-us' ? 'Learn more' : 'Leer más'),
                        ribbon: item.data.ribbon || '',
                        prismicCategory: prismicCategory
                    };
                });

            // Si no hay filtros activos, retornar todos
            if (!this.hasActiveFilters) {
                return items;
            }

            // Obtener categorías de Prismic seleccionadas
            const selectedPrismicCategories = this.tags
                .filter((tag, idx) => this.isActiveTag[idx])
                .map(tag => tag.prismicCategory);

            // Filtrar por categorías seleccionadas (comparar con la categoría de Prismic)
            return items.filter(item =>
                selectedPrismicCategories.includes(item.prismicCategory)
            );
        }
    },

    methods: {
        setLanguage() {
            if (this.$route.path.startsWith('/es') || this.$route.path.startsWith('/en')) {
                this.prismicLang = this.$route.path.startsWith('/es') ? 'es-es' : 'en-us';
                return;
            }

            const browserLang = navigator.language || navigator.userLanguage; 
            let path = this.$route.path;
            let query = this.$route.query;

            const preferredLang = browserLang.startsWith('es') ? 'es' : 'en';
            const newPath = `/${preferredLang}${path}`;

            if (newPath !== path) {
                this.$router.replace({ path: newPath, query });
            }

            this.prismicLang = preferredLang === 'es' ? 'es-es' : 'en-us';
        },

        updateMeta() {
            const title = this.translations.pageTitle;
            const content = this.translations.pageDescription;
            
            const meta = document.querySelector('meta[name="description"]');
            if (meta) {
                meta.setAttribute('content', content);
            } else {
                const metaTag = document.createElement('meta');
                metaTag.setAttribute('name', 'description');
                metaTag.setAttribute('content', content);
                document.head.appendChild(metaTag);
            }

            document.title = title;
        },

        async toggleLanguage() {
            this.sendSwitchLanguage();

            const currentQuery = this.$route.query;
            const currentPath = this.$route.path;
            let newPath = currentPath;

            if (this.prismicLang === 'en-us') {
                newPath = currentPath.replace(/^\/en/, '/es');
                this.prismicLang = 'es-es';
            } else {
                newPath = currentPath.replace(/^\/es/, '/en');
                this.prismicLang = 'en-us';
            }

            if (newPath !== currentPath) {
                this.$router.push({ path: newPath, query: currentQuery });
            }

            this.updateMeta();
            await this.retrieveData();
            this.sendPageView();
        },

        scrollToNews() {
            const newsElement = document.getElementById('news');
            if (newsElement) {
                const elementPosition = newsElement.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - 10;
                
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        },

        scrollToTop() {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        },

        // SEO and Analytics Methods
        sendPageView() {
            console.log("sendPageView");
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                'event': 'pageview',
                'language': this.prismicLang || 'unknown'
            });
        },

        sendSwitchLanguage() {
            console.log("sendSwitchLanguage");
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                'event': 'switch_language',
                'button_text': this.prismicLang === 'es-es' ? "Switch to English" : "Cambiar a Español"
            });
        },

        // NEWS FILTERING METHODS
        toggleTag(idx) {
            // Toggle tag activo/inactivo
            this.$set(this.isActiveTag, idx, !this.isActiveTag[idx]);
        },

        clearAllFilters() {
            // Desactivar todos los filtros
            this.isActiveTag = this.tagsBase.map(() => false);
        },

        translateCategory(category) {
            // Traducir categorías según el idioma
            const isEnglish = this.prismicLang === 'en-us';
            
            const categoryTranslations = {
                'Events': isEnglish ? 'Events' : 'Eventos',
                'Studying tips': isEnglish ? 'Studying tips' : 'Consejos de estudio',
                'Partnerships': isEnglish ? 'Partnerships' : 'Alianzas',
                'Features': isEnglish ? 'Features' : 'Funcionalidades',
                'Success stories': isEnglish ? 'Success stories' : 'Casos de éxito',
                'Learning essentials': isEnglish ? 'Learning essentials' : 'Recursos esenciales'
            };
            
            return categoryTranslations[category] || category;
        },

        getNewsUrl(newsId) {
            const lang = this.prismicLang === 'es-es' ? 'es' : 'en';
            return `/${lang}/news/${newsId}`;
        },

        // PRISMIC DATA METHODS
        getCenterParam() {
            const urlParams = new URLSearchParams(window.location.search);
            this.clcSegmentValue = urlParams.get(Const.CENTER) ?? this.clcSegmentValue;
        },

        async getHomePageData() {
            try {
                const options = {
                    lang: this.prismicLang,
                };

                const response = await this.$prismic.client.getSingle('clc_new_home_page', options);
                this.homePageDocument = response;
                this.homePageData = response?.data;
            } catch (error) {
                console.error("METHOD: getHomePageData ==> ", error);
            }
        },

        getBannerData() {
            const data = this.homePageData;
            this.bannerData = {
                title: data?.title?.[0]?.text || this.bannerData.title,
                description: data?.description?.[0]?.text || this.bannerData.description,
                icons: data?.categories || []
            };
        },

        getCintilloData() {
            this.cintilloData = {
                enabled: false,
                title: '',
                description: '',
                link: '',
                blank: false,
                linkText: '',
                imageDesktop: '',
                imageMobile: ''
            };

            const data = this.homePageData;

            // Primero buscar cintillo local específico para este segment
            const lookUpLocal = data?.cintillos_locales?.find(cintillo =>
                cintillo.segment_cintillo_local === this.clcSegmentValue &&
                cintillo.cintillo_local_on_off === true &&
                cintillo.titulo_cintillo_local?.[0]?.text
            );

            if (lookUpLocal) {
                this.cintilloData = {
                    enabled: true,
                    title: lookUpLocal?.titulo_cintillo_local?.[0]?.text || '',
                    description: lookUpLocal?.body_text?.[0]?.text || '',
                    link: lookUpLocal?.link?.url || '',
                    linkText: lookUpLocal?.link_text?.[0]?.text || '',
                    blank: lookUpLocal?.link?.target === '_blank',
                    imageDesktop: getCleanPrismicImageUrl(lookUpLocal?.image_local?.url),
                    imageMobile: getCleanPrismicImageUrl(lookUpLocal?.image_local?.mobile?.url)
                };
            } else if (data?.cintillo_general_on_off && data?.titulo_cintillo_general?.[0]?.text) {
                // Si no hay cintillo local, usar el cintillo general
                this.cintilloData = {
                    enabled: true,
                    title: data.titulo_cintillo_general?.[0]?.text || '',
                    description: data?.default_body_text?.[0]?.text || '',
                    link: data?.default_link?.url || '',
                    linkText: data?.default_link_text?.[0]?.text || '',
                    imageDesktop: getCleanPrismicImageUrl(data?.image_general?.url),
                    blank: data?.default_link?.target === '_blank',
                    imageMobile: getCleanPrismicImageUrl(data?.image_general?.mobile?.url)
                };
            }
        },

        // Category helper methods (same as CLCHomePage.vue)
        getCategoryTitle(icon) {
            return icon?.image_title?.[0]?.text || '';
        },

        getCategoryURL(icon) {
            return icon?.link?.url || '';
        },

        getCategoryImage(icon) {
            return getCleanPrismicImageUrl(icon?.image?.url);
        },

        getCategoryImageAlt(icon) {
            return icon?.image?.alt || '';
        },

        getCategoryIfTargetBlank(icon) {
            return icon?.link?.target ?? '';
        },

        getCategoryDescription(icon) {
            return icon?.description?.[0]?.text || '';
        },

        async getQuickLinksData() {
            if (this.homePageData) {
                this.quicklinksData = {
                    fullyLoaded: true,
                    title: this.homePageData.title_quicklinks?.[0]?.text ?? '',
                    items: this.homePageData.quicklinks ?? []
                }
            }
        },

        getQuicklinkTitle(item) {
            return item?.title_link?.[0]?.text || '';
        },

        getQuicklinkURL(item) {
            return item?.link?.url || '';
        },

        getQuicklinkImage(item) {
            let imageUrl = item?.image?.url || '';
            if (imageUrl.includes('?auto=compress,format')) {
                imageUrl = imageUrl.replace('?auto=compress,format', '');
            }
            return imageUrl;
        },

        getQuicklinkImageAlt(item) {
            return item?.image?.alt || '';
        },

        getQuicklinkIfTargetBlank(item) {
            return item?.link?.target ?? '';
        },

        async getNewsData() {
            try {
                this.isLoadingInitialNews = true;

                const options = {
                    page: this.currentPage,
                    pageSize: 3,
                    lang: this.prismicLang,
                    orderings: '[document.last_publication_date desc]'
                };

                const response = await this.$prismic.client.query([
                    this.$prismic.predicate.at(Const.DOCUMENT_TYPE, Const.CLC_NEWS),
                    this.$prismic.predicate.any('my.clc_news.targetings.clc_segment', [Const.GLOBAL, this.clcSegmentValue])
                ], options);

                this.newsDocument = response;
                this.newsData = response?.results;

                // NO incrementar currentPage aquí - lo hacemos en infiniteHandler después de verificar
                // if (this.currentPage < response?.total_pages) {
                //     this.currentPage++;
                // }
            } catch (error) {
                console.error("METHOD: getNewsData ==> ", error);
            } finally {
                this.isLoadingInitialNews = false;
            }
        },

        async infiniteHandler($state) {
            try {
                // Si currentPage es 1, significa que ya cargamos la página inicial, verificar si hay más
                if (this.currentPage === 1) {
                    // Verificar si hay más de 1 página
                    if ((this.newsDocument?.total_pages || 1) > 1) {
                        // Hay más páginas, incrementar y cargar
                        this.currentPage++;
                    } else {
                        // Solo hay 1 página, terminar
                        $state.complete();
                        return;
                    }
                }

                // Guardar las noticias actuales antes de cargar más
                const currentNews = this.newsData || [];

                // Cargar la siguiente página
                await this.getNewsData();

                // Concatenar las noticias anteriores con las nuevas
                const newNews = this.newsData || [];
                this.newsData = [...currentNews, ...newNews];

                // Verificar si hay más páginas después de esta
                if (this.currentPage < (this.newsDocument?.total_pages || 0)) {
                    // Hay más páginas, incrementar y decir que cargamos
                    this.currentPage++;
                    $state.loaded();
                } else {
                    // Esta fue la última página
                    $state.complete();
                }
            } catch (error) {
                console.error("METHOD: infiniteHandler ==> ", error);
                $state.complete();
            }
        },

        async retrieveData() {
            this.currentPage = 1;
            this.homePageDocument = null;
            this.newsDocument = null;
            this.homePageData = null;
            this.newsData = null;

            await this.getHomePageData();
            await this.getQuickLinksData();
            await this.getNewsData();
            this.getBannerData();
            this.getCintilloData();
        },

        getAndSetCLCName(){
            if (this.homePageData?.clc_list && this.clcSegmentValue) {
                const matchedCLC = this.homePageData.clc_list.find(clc => clc.clc_id === this.clcSegmentValue);
                if (matchedCLC) {
                    this.clcName = matchedCLC.clc_name;
                }
            }
            sessionStorage.setItem('clcSegmentValue', this.clcSegmentValue);
            sessionStorage.setItem('clcName', this.clcName);
        },
    },

    mounted() {
        this.sendPageView();
        // Guardar la ruta actual de la homepage en sessionStorage
        sessionStorage.setItem('lastHomepage', this.$route.path);
    },

    async created() {
        this.setLanguage();
        this.updateMeta();
        this.getCenterParam();
        await this.retrieveData();
        this.getAndSetCLCName();
        // Inicializar todos los tags como inactivos
        this.isActiveTag = this.tagsBase.map(() => false);
    }
}
</script>

<style scoped>
/* Custom Loader Animation */
.custom-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px 0;
}

.spinner-svg {
    animation: spin 1.5s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

/* Infinite Loading Container */
.infinite-loading-container {
    margin-top: 20px;
}
</style>